import React from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";

const CtaSection: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, margin: "-100px" });

  const fadeInUp = {
    initial: {
      opacity: 0,
      y: 30,
      scale: 0.98,
    },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const pulse = {
    animate: {
      scale: [1, 1.05, 1],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  return (
    <>
      {/* Main CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 relative overflow-hidden">
        {/* Animated Background Gradients */}
        <div className="absolute inset-0 bg-gradient-to-r from-pink-600/20 via-purple-600/20 to-blue-600/20 animate-pulse"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-transparent via-cyan-500/10 to-transparent"></div>

        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            ref={ref}
            className="text-center"
            variants={staggerContainer}
            initial="initial"
            animate={isInView ? "animate" : "initial"}
          >
            <motion.div variants={fadeInUp}>
              <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
                신용카드 현금화가 필요할 때는
              </h2>
              <h3 className="text-3xl md:text-4xl font-bold text-blue-200 mb-8">
                이노페이 24시간 신속하고 간편하게!
              </h3>
              <div className="w-32 h-1 bg-white mx-auto rounded-full mb-12 shadow-lg"></div>
            </motion.div>

            <motion.div
              className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16"
              variants={fadeInUp}
            >
              <motion.a
                href="tel:010-9733-3651"
                className="group bg-gradient-to-r from-gray-100 to-white hover:from-white hover:to-gray-100 text-gray-800 px-12 py-6 rounded-2xl text-2xl font-bold shadow-2xl hover:shadow-3xl hover:shadow-white/30 transition-all duration-300 flex items-center gap-4 hover:scale-105 relative overflow-hidden"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                variants={pulse}
                animate={pulse.animate}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white to-gray-100 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative flex items-center gap-4">
                  <svg
                    className="w-8 h-8"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                  <div>
                    <div className="text-sm opacity-90">클릭시 자동연결</div>
                    <div className="font-bold">유선문의: 010-9733-3651</div>
                  </div>
                </div>
              </motion.a>
            </motion.div>

            {/* Features Grid */}
            <motion.div
              className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
              variants={fadeInUp}
            >
              <div className="bg-gradient-to-br from-blue-900/30 to-purple-900/30 backdrop-blur-sm rounded-2xl p-6 border border-white border-opacity-30 shadow-lg hover:shadow-xl hover:shadow-white/20 transition-all duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-white bg-opacity-20 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <h4 className="text-xl font-semibold text-white mb-2">
                    365일 24시간
                  </h4>
                  <p className="text-blue-100">언제 어디서든 이용 가능</p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-900/30 to-purple-900/30 backdrop-blur-sm rounded-2xl p-6 border border-white border-opacity-30 shadow-lg hover:shadow-xl hover:shadow-white/20 transition-all duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-white bg-opacity-20 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <h4 className="text-xl font-semibold text-white mb-2">
                    업계 최저 수수료
                  </h4>
                  <p className="text-blue-100">고객님들께 약속합니다</p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-900/30 to-purple-900/30 backdrop-blur-sm rounded-2xl p-6 border border-white border-opacity-30 shadow-lg hover:shadow-xl hover:shadow-white/20 transition-all duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-white bg-opacity-20 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                  <h4 className="text-xl font-semibold text-white mb-2">
                    최대 3분 입력금
                  </h4>
                  <p className="text-blue-100">신속 정확한 빠른입금</p>
                </div>
              </div>
            </motion.div>

            {/* Final Message */}
            <motion.div
              className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm rounded-3xl p-8 border border-white border-opacity-20 shadow-lg"
              variants={fadeInUp}
            >
              <h4 className="text-2xl font-bold text-white mb-4 text-center">
                간편한 카드 현금화 절차
              </h4>
              <p className="text-blue-100 text-lg text-center leading-relaxed mb-6">
                이노페이 업계 최저 수수료로 카드 현금화 후 입금까지 최대
                3분안으로
                <br />
                신속 정확한 빠른입금! 무이자 할부로 카드값 부담없이!
              </p>
              <div className="text-center mb-4">
                <p className="text-gray-300 text-sm">
                  🔍 검색 키워드: 신용카드 현금화 현금화|현금서비스|대출 대안
                  |무이자 할부|신용조회 없음|24시간 서비스|업계 최저 수수료
                </p>
              </div>
              <div className="text-center">
                <p className="text-white text-xl font-semibold">
                  무이자할부 지원으로 이노페이는 업계 최저 수수료를
                  <br />
                  고객님들께 약속합니다!
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap- selector-none">
            <div className="text-center mb-8">
              <h5 className="text-2xl font-bold mb-4">이노페이</h5>
              <p className="text-gray-300 leading-relaxed">
                신용카드 현금화 전문 서비스
                <br />
                빠르고 안전한 현금화 솔루션
              </p>
            </div>

            <div className="text-center mb-8">
              <h6 className="text-lg font-semibold mb-4 text-blue-400">
                연락처
              </h6>
              <div className="space-y-2">
                <p className="text-gray-300">유선문의: 010-9733-3651</p>
              </div>
            </div>

            <div className="text-center">
              <h6 className="text-lg font-semibold mb-4 text-blue-400">
                서비스 특징
              </h6>
              <div className="space-y-1 text-sm text-gray-300">
                <p>• 신용조회 없음</p>
                <p>• 타인 카드 사용 불가</p>
                <p>• 본인 명의 카드만 가능</p>
                <p>• 24시간 상담 및 접수</p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8">
            <div className="text-center text-gray-400">
              <p>&copy; 2025 이노페이. 모든 권리 보유.</p>
              <p className="mt-2 text-sm">
                신용카드 현금화, 카드한도 내 현금화 서비스 제공
              </p>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default CtaSection;
